// type BaseInsuranceCoverageId = 'primaryCoverage' | 'medicaidCoverage'

import { EligibilityResponse } from '../claims/eligibility'
import { RealtimeEligibilityRequest } from '../claims/EligibilityRequestArgs'
import { FileDBValue } from '../data'
import { UserGroup, WithMetadata } from '../shared/db'
import { Address } from '../utils/medical'
import { InsurancePlan, InsuranceProvider } from './insurer'

export type CoverageType = 'medicaid' | 'non-medicaid'
export type CoverageId = 'primary' | 'secondary'
export type PatientCoverageLabel = CoverageId | 'additional'
export type AdditionalCoverageId = `additional.${string}`
export type PatientCoverageId = CoverageId | AdditionalCoverageId

export type PregnancyCoverageLabel = CoverageId | 'potential'
export type WithPatientCoverageId<T> = T & { id: PatientCoverageId }

export type ReimbursementOption = 'medicare-rates' | 'r-and-c' | 'unknown'
export type PlanDesignOption = 'fully-insured' | 'split-funded' | 'self-funded'

export interface SentEmailData {
  on: number
  by: string
  message: string
}

export interface InsuranceCoverageRequest {
  type: 'request'
  withCallInForm: boolean
  requestedOn: number
  requestedBy: string
  requestedByGroup: UserGroup
  message: string
  emailsSent: SentEmailData[]
}

export type InsuranceCoverageUpdateType =
  | 'basicInfo'
  | 'callIn'
  | 'policyOwner'
  | 'unsetAsPrimary'
  | 'setAsPrimary'
  | 'setAsSecondary'
  | 'unsetAsSecondary'
export interface InsuranceCoverageHistory {
  type: InsuranceCoverageUpdateType
  on: number
  by: string
  group: UserGroup
  notes?: string
}

export interface CoverageEligibilityRequest {
  response: EligibilityResponse
  request: RealtimeEligibilityRequest & {
    practiceId: string
    practiceName: string
    insurerId: string
    insurerName: string
  }
  // plan: Omit<BaseInsuranceCoverage, 'eligibilityRequests'>
}

export interface CallInRequest {
  on: number
  by: string
  message: string
  emailsSent: SentEmailData[]
}

export interface PolicyOwner {
  policyOwnerName?: {
    fname: string
    lname: string
  }
  policyOwnerSex?: string
  policyOwnerDob?: number
  policyOwnerSameAddress?: boolean
  policyOwnerAddress?: Address
  policyOwnerPhone?: string
  policyOwnerEmail?: string
}

export interface CoveredService {
  serviceCoverage: 'unlimited' | 'limited' | 'not-covered'
  notes?: string
}

export interface CoveredServices {
  acupuncture?: CoveredService
  chiropractic?: CoveredService
  'physical-therapy'?: CoveredService
}

export interface BaseInsuranceCoverage {
  type?: never
  isMedicaid: boolean
  insuranceProviderId?: string
  // currentlyOnMedicaidPlan?: boolean
  medicaidStartDate?: number | null
  initialServiceDate?: number | null
  planName?: string
  noMemberId?: boolean
  callInRequests?: Record<string, CallInRequest>
  fromRequest?: InsuranceCoverageRequest | null
  memberId?: string
  policyOwnerRelationship?: string
  policyOwnerInfo?: PolicyOwner
  terminationDate?: number
  insuranceProviderNumber?: string
  proofOfFutureCoverage?: FileDBValue | null
  insuranceCard?: {
    front?: FileDBValue
    frontImageIncludesBack?: boolean
    back?: FileDBValue
  }
  address?: Address
  planDocuments?: FileDBValue[]
  'in-network'?: {
    coinsurance: number
    deductible: number
    deductibleBasedOnCalendarYear: boolean
    deductibleCountsTowardOOPM: boolean
    outOfPocketMax: number
    notes?: string
    coveredServices?: CoveredServices
  }
  'out-of-network'?: {
    coinsurance: number
    deductible: number
    deductibleCountsTowardOOPM: boolean
    outOfPocketMax: number
    maximumReimbursableCharges: {
      amount?: number
      notes?: string
    }
    noBenefits?: boolean
    reimbursementOption: {
      optionText: ReimbursementOption
      medicareRate?: number
      reasonableAndCustomaryRate?: number
      notes?: string
    }
  }
  'plan-design-and-state-mandates'?: {
    planDesign: PlanDesignOption
    followsStateMandates: boolean
    confirmFollowsStateMandates?: boolean
    confirmDoesNotFollowStateMandates?: boolean
    // state plan is based in - legacy prop name, should be changed to planState
    followsStateMandatesNote?: string
    notes?: string
  }
  'plan-covers-midwives'?: {
    coveredServices?: CoveredServices
    coversMidwives?: boolean
    notes?: string
  }
  'call-details'?: {
    agentName: string
    callReferenceNumber: string
    notes?: string
  }
  eligibilityRequests?: Record<string, WithMetadata<CoverageEligibilityRequest>>
  history?: InsuranceCoverageHistory[]
}

export type CoverageWithMetadata = WithMetadata<BaseInsuranceCoverage> & {}

export interface InsuranceCoverage
  extends WithMetadata<Omit<BaseInsuranceCoverage, 'insuranceProvider' | 'id'>> {
  insuranceProvider: InsuranceProvider | null
  plan: InsurancePlan | null
  id: PatientCoverageId
}

export const isInsuranceCoverageRequest = (
  c: BaseInsuranceCoverage | InsuranceCoverageRequest,
): c is InsuranceCoverageRequest => c.type === 'request'

export type PregnancyCoverage<T> = T & {
  pregnancyLabel: PregnancyCoverageLabel
}

export const isPregnancyCoverage = (
  c: InsuranceCoverage | PregnancyCoverage<InsuranceCoverage>,
): c is PregnancyCoverage<InsuranceCoverage> => 'pregnancyLabel' in c
